import { useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCart } from "@/contexts/CartContext";
import { useQueryClient } from "@tanstack/react-query";
import { trackPurchase, trackPurchaseCanceled, cartItemToAnalytics } from "@/lib/analytics";
import { consumeExpressCheckoutFlag } from "@/lib/expressCheckoutFlag";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const usePaymentVerification = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { clearCart, items, discountedTotal, discount } = useCart();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const verifiedRef = useRef(false);

  useEffect(() => {
    if (verifiedRef.current) return;

    const sessionId = searchParams.get("session_id");
    const success = searchParams.get("success");
    const canceled = searchParams.get("canceled");
    const paysera = searchParams.get("paysera");

    const bookIdsParam = searchParams.get("book_ids");
    const bookIdParam = searchParams.get("book_id");
    const bookIds = bookIdsParam ? bookIdsParam.split(",").filter(Boolean) : bookIdParam ? [bookIdParam] : [];
    const lessonIdsParam = searchParams.get("lesson_ids");
    const lessonIds = lessonIdsParam ? lessonIdsParam.split(",").filter(Boolean) : [];
    const payseraOrderId = searchParams.get("paysera_order_id") || "";

    const verify = async (sid: string, bIds: string[], lIds: string[], isPaysera = false) => {
      verifiedRef.current = true;

      // Always show optimistic success — webhooks (Stripe / Paysera callback) are the
      // source of truth and process the order server-side. Never show payment errors
      // to the customer here, even if verify-book-payment hiccups.
      const totalItems = bIds.length + lIds.length;
      toast({
        title: "Mokėjimas sėkmingas!",
        description: totalItems > 1
          ? `Įsigyti ${totalItems} produktai. Patvirtinimas išsiųstas el. paštu.`
          : "Užsakymas priimtas. Patvirtinimas išsiųstas el. paštu.",
      });

      // GA4 tracking (cart still has items here)
      try {
        const analyticsItems = items.map(i => cartItemToAnalytics(i.product, i.quantity));
        if (analyticsItems.length > 0) {
          trackPurchase({
            transactionId: sid,
            value: discountedTotal,
            items: analyticsItems,
            coupon: discount?.code || null,
            paymentMethod: isPaysera ? "paysera" : "stripe",
          });
        }
      } catch (e) {
        console.warn("[analytics] purchase tracking failed", e);
      }

      // Fire-and-forget verification call — silent on failure
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          if (attempt > 1) {
            await supabase.auth.refreshSession();
            await sleep(RETRY_DELAY_MS);
          }
          const { error } = await supabase.functions.invoke("verify-book-payment", {
            body: { sessionId: sid, bookIds: bIds, lessonIds: lIds, isPaysera },
          });
          if (error) {
            const isAuthError = error.message?.includes("401") || error.message?.includes("Unauthorized");
            if (isAuthError && attempt < MAX_RETRIES) {
              console.warn(`[verify] Auth error on attempt ${attempt}, retrying...`);
              continue;
            }
            console.warn("[verify] non-auth error (silent):", error);
          }
          break;
        } catch (e) {
          console.warn(`[verify] attempt ${attempt} threw (silent):`, e);
        }
      }

      // An express purchase never contained the stored cart — leave it alone.
      if (!consumeExpressCheckoutFlag()) clearCart();
      queryClient.invalidateQueries({ queryKey: ["book-purchases"] });
      navigate("/knygynas", { replace: true });
    };


    if (success === "true" && sessionId) {
      verify(sessionId, bookIds, lessonIds);
    } else if (canceled === "true") {
      verifiedRef.current = true;
      trackPurchaseCanceled("user_canceled");
      toast({ title: "Mokėjimas atšauktas", variant: "destructive" });
      navigate("/knygynas", { replace: true });
    } else if (paysera === "success") {
      verify(payseraOrderId || "paysera", bookIds, lessonIds, true);
    } else if (paysera === "canceled") {
      verifiedRef.current = true;
      trackPurchaseCanceled("user_canceled");
      toast({ title: "Mokėjimas atšauktas", variant: "destructive" });
      navigate("/knygynas", { replace: true });
    }
  }, [searchParams, toast, navigate, clearCart, queryClient, items, discountedTotal, discount]);
};
